import { getLocalStorage, removeLocalStorage } from '@/utils/common/storage'
import { goPage } from '@/utils/browser/page'

import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import type { ApiResponseModel } from '@/utils/api/models'
import axios from 'axios'
import { api } from '@/services'
import { env } from '@/env'
import { getI18nTranslate, hasI18nTranslation } from '@/utils/i18nUtils'
import { errorCodeHandler, unauthorizedHandler } from '@/utils/api/error-handler'
import { useMessage } from '@/composables/useMessage'
import { useUserStore } from '@/stores/user.stores'
import { LoginEnum, SignUpEnum } from '@/constants/enums/api/auth.enums'
import {
  STORE_INFO,
  FROM_THIRD_PARTY_ID,
  FROM_THIRD_PARTY_TYPE,
  CONTEXT_SWITCH_USER_ID
} from '@/constants/storageKey.const'
import { isIOSSafari, getDeviceInfo } from '@/utils/device-detection'

/**
 * 創建實例。
 * @remarks 認證走 Authorization header（Bearer token），不需要 withCredentials 讓瀏覽器
 *          帶 cookie（OP#2616）：目前設 true 是多餘設定，且若之後後端改用 cookie 存任何資訊，
 *          這裡已經是 true 反而會在沒人特別決定的情況下悄悄變成真實的 CSRF 暴露面。
 */
const axiosInstance = axios.create({
  baseURL: `${env.apiBaseUrl}${env.apiVersion}`,
  timeout: 30000,
  withCredentials: false
})

/**
 * 請求發送前的攔截處理。
 *
 * 為非認證 API 加上 Authorization header；認證 API（登入、Google 登入、註冊等）
 * 本身不需要 token，若帶上反而可能觸發 server 端 401 驗證迴圈，故排除。
 */
const beforeRequest = (config: InternalAxiosRequestConfig) => {
  const { token, userInfo } = useUserStore()
  const { login, googleLogIn } = LoginEnum
  const { signIn, validate, forgotPassword } = SignUpEnum

  // 認證相關 API 不帶 Bearer token，避免 server 對尚未登入的請求回傳 401 造成迴圈
  const isAuthApi = [login, googleLogIn, signIn, validate, forgotPassword].some((apiPath) =>
    config.url?.includes(apiPath)
  )

  if (!isAuthApi) {
    // 未登入時 token 是空字串，不該送出 Authorization: Bearer （空字串）（OP#2614）
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const delegatedUser = getLocalStorage<{ userId: string }>(CONTEXT_SWITCH_USER_ID)
    const delegatedUserId = delegatedUser?.userId ?? null
    const systemVersion = getLocalStorage<{ isNewSystem: boolean }>('systemVersion', true)
    const isNewSystem = systemVersion?.isNewSystem ?? false
    if (delegatedUserId && delegatedUserId !== userInfo.userId && isNewSystem) {
      config.headers['Delegated-Account'] = delegatedUserId
    }
  }

  config.headers['Content-Type'] = 'application/json;charset=UTF-8'

  return config
}

/** 處理請求錯誤 */
const requestFailed = (error: AxiosError) => {
  return Promise.reject(error)
}

/**
 * HTTP 狀態碼 2xx 的回應攔截處理。
 *
 * 即使 HTTP 層成功，後端仍可能在 body 中回傳業務層錯誤（`isSuccess: false`）。
 * 另需特別處理 `resultCode 1002`：表示 token 已在後端失效（如遠端登出或帳號停用），
 * 需主動清除本地狀態並強制導回登入頁。
 */
const responseSuccess = (response: AxiosResponse) => {
  if (!response.data.isSuccess) {
    return response.data
  }

  // resultCode 1002：後端通知 token 已失效（非 HTTP 層的 401），需主動登出並清除第三方登入資訊
  if (response.data.resultCode === 1002) {
    api.auth.logout().then(() => {
      useUserStore().initLoginState()
      removeLocalStorage(STORE_INFO)
      removeLocalStorage(FROM_THIRD_PARTY_ID)
      removeLocalStorage(FROM_THIRD_PARTY_TYPE)
      goPage('/login')
    })
  }

  return response.data
}

/**
 * HTTP 狀態碼非 2xx 的回應攔截處理。
 *
 * 處理順序：
 * 1. 若為主動取消的請求（`AbortController`），直接 reject，不顯示錯誤訊息
 * 2. 若有 HTTP response：先交由 `unauthorizedHandler` 判斷 401（需清除登入狀態並導向登入頁）；
 *    403 代表已登入但沒有權限，顯示拒絕存取提示並 reject，不強制登出；
 *    其餘錯誤碼由 `errorCodeHandler` 對應 i18n 訊息後顯示
 * 3. 若無 response（網路層錯誤）：檢查網路狀態後顯示對應提示
 */
const responseFailed = async (error: AxiosError) => {
  // 主動取消的請求（例如元件 unmount 時）不顯示錯誤提示，直接 reject
  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  const { openMessage } = useMessage()
  const { response } = error

  if (response) {
    const { status, data } = response
    // 401 由 unauthorizedHandler 處理（清除本地登入狀態並導向登入頁）
    const unauthorized = unauthorizedHandler(status)
    if (unauthorized) return unauthorized

    // 403：已登入但沒有權限做這個操作，跟 401（未登入/token 失效）語意不同，不清除登入狀態、
    // 不導頁；resultCode 若有對應的業務錯誤訊息（例如 C04：無操作權限）要優先顯示，比通用的
    // 「拒絕存取」更明確，查無對應翻譯時才退回通用訊息；一定要 reject，避免呼叫端把
    // 這個失敗回應當成正常資料繼續處理（OP#2607：先前這裡落到下面的 resolve 分支）
    if (status === 403) {
      const resultCodeKey = `ServerResponse.ResultCode.${(data as ApiResponseModel)?.resultCode}`
      const message = hasI18nTranslation(resultCodeKey)
        ? getI18nTranslate(resultCodeKey)
        : getI18nTranslate('Common.Response.Forbidden')

      openMessage('error', message)
      return Promise.reject(new Error(message))
    }

    // 其他 HTTP 錯誤碼顯示對應的 i18n 錯誤訊息
    errorCodeHandler(status, (data as ApiResponseModel).message)
    return Promise.resolve(data as ApiResponseModel)
  }

  /** 檢查網路連線 */
  const online = window.navigator.onLine
  if (!online) {
    openMessage('error', getI18nTranslate('Common.Response.NoNetwork'))
    return Promise.reject(new Error(getI18nTranslate('Common.Response.NoNetwork')))
  }

  openMessage('error', getI18nTranslate('Common.Response.ServerError'))
  return Promise.reject(new Error(getI18nTranslate('Common.Response.ServerError')))
}

/** 請求攔截器 */
axiosInstance.interceptors.request.use(beforeRequest, requestFailed)

/** 回傳攔截器 */
axiosInstance.interceptors.response.use(responseSuccess, responseFailed)

export default axiosInstance
